/**
 * BuildersDeclarationForm.js
 * "Builder's Declaration" form within the OC Submission tab.
 *
 * Recreates the NSW Installation Certificate — Builder's Completion Certificate.
 * - Project Details: editable, "Development Address" syncs from project.site_address on first load.
 * - Standards of Performance: fixed Builder's Completion text + editable sub-fields.
 * - Certifying Builder: pre-filled with Revolead Construction defaults (editable), live signature, date.
 */

import { useState, useEffect, useRef } from 'react';
import SignaturePicker from '../../quote/SignaturePicker';
import { exportBuildersDeclarationPdf } from '../../../lib/builderDeclarationPdfExport';

const NAVY = '#1a1a2e';
const GOLD = '#c9a84c';

const DEFAULTS = {
  cc_cdc_number: '',
  development_consent_no: '',
  building_part_description: '',
  development_address: '',

  access_report: { number: '', revision: '', date: '', prepared_by: '' },
  fire_engineering_report: { number: '', revision: '', date: '', prepared_by: '' },
  exclusions_limitations: '',
  other_practices_documents: '',
  copies_attached: '',

  builder_name: 'Liangliang Zheng',
  builder_company: 'Revolead Construction',
  builder_abn: '65 696 557 614',
  builder_phone: '0452 229 888',
  builder_address: '27 George St, Clyde, NSW, 2142',
  builder_licence: '383917C',
  builder_licence_type: 'Commercial Builder',
  signature: '',
  date: '',
};

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

function formatDateLong(dateStr) {
  if (!dateStr) return '';
  try {
    return new Date(`${dateStr}T00:00:00`).toLocaleDateString('en-AU', {
      day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch {
    return dateStr;
  }
}

// ── Field treatment: invisible → dashed gold (hover) → solid gold + cream (focus) ──
const fieldClass =
  'w-full bg-transparent text-sm px-2 py-1 rounded-sm border border-transparent ' +
  'transition-colors outline-none ' +
  'hover:border hover:border-dashed hover:border-[#c9a84c] ' +
  'focus:border-solid focus:border-[#c9a84c] focus:bg-[#fdf8ee]';

function EditableInput({ value, onChange, mono, placeholder }) {
  return (
    <input
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      className={`${fieldClass} ${mono ? 'font-mono' : ''}`}
    />
  );
}

function EditableTextarea({ value, onChange, placeholder }) {
  const ref = useRef(null);
  const grow = (el) => {
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => { grow(ref.current); }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      rows={2}
      className={`${fieldClass} resize-none overflow-hidden`}
    />
  );
}

function SectionBar({ title }) {
  return (
    <div
      className="px-3 py-1.5 text-xs font-bold uppercase tracking-widest"
      style={{ background: GOLD, color: NAVY }}
    >
      {title}
    </div>
  );
}

function Row({ label, children, italic }) {
  return (
    <tr>
      <td
        className="align-top text-xs font-semibold p-3 w-1/3"
        style={{ color: NAVY, borderBottom: '1px solid #e5e4df', fontStyle: italic ? 'italic' : 'normal' }}
      >
        {label}
      </td>
      <td className="align-top p-2" style={{ borderBottom: '1px solid #e5e4df' }}>
        {children}
      </td>
    </tr>
  );
}

export default function BuildersDeclarationForm({ project, data, onChange }) {
  const initialized = useRef(false);
  const [exporting, setExporting] = useState(false);

  // First-load sync: default date to today, sync development address from project
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;
    const patch = {};
    if (!data.date) patch.date = todayISO();
    if (!data.development_address && project?.site_address) {
      patch.development_address = project.site_address;
    }
    if (Object.keys(patch).length > 0) {
      onChange({ ...data, ...patch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const d = { ...DEFAULTS, ...data };

  const set = (field, value) => onChange({ ...d, [field]: value });
  const setNested = (field, sub, value) => onChange({ ...d, [field]: { ...d[field], [sub]: value } });

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportBuildersDeclarationPdf(d, project);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="bg-white border-t-4 shadow-sm" style={{ borderColor: NAVY }}>
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="p-6 sm:p-10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em]" style={{ color: GOLD }}>
              Installation Certificate
            </p>
            <h2 className="text-2xl font-bold mt-1" style={{ color: NAVY, fontFamily: "'Space Grotesk', sans-serif" }}>
              Builder's Completion Certificate
            </h2>
          </div>
          <button
            onClick={handleExport}
            disabled={exporting}
            className="px-4 py-2 rounded text-xs font-semibold disabled:opacity-50 transition-opacity"
            style={{ background: NAVY, color: GOLD }}
          >
            {exporting ? 'Generating…' : 'Export PDF'}
          </button>
        </div>
        <div className="h-0.5 mt-3 mb-4" style={{ background: GOLD }} />
        <p className="text-sm font-semibold mb-2" style={{ color: NAVY }}>I hereby certify that:</p>
        <p className="text-sm leading-relaxed mb-2" style={{ color: '#2c2c2c' }}>
          <strong>(a)</strong> The building work has been completed or inspected during construction and complies
          with the approved Construction Certificate / Complying Development Certificate documentation, plans,
          specifications, any other documents approved in writing by the Principal Certifier, and the nominated
          Standards of Performance listed below.
        </p>
        <p className="text-sm italic" style={{ color: '#6b7280' }}>
          The information contained in this statement is true and accurate to the best of my knowledge.
        </p>
      </div>

      {/* ── Project Details ──────────────────────────────────────────────── */}
      <SectionBar title="Project Details" />
      <table className="w-full border-collapse">
        <tbody>
          <Row label="CC / CDC Number (Including Modifications)">
            <EditableInput mono value={d.cc_cdc_number} onChange={v => set('cc_cdc_number', v)} placeholder="e.g. 25000303" />
          </Row>
          <Row label="Development Consent No. (Including Modifications)">
            <EditableInput mono value={d.development_consent_no} onChange={v => set('development_consent_no', v)} />
          </Row>
          <Row label="Detail Description of The Building Part to be Certified (Whole / Part)">
            <EditableTextarea value={d.building_part_description} onChange={v => set('building_part_description', v)} />
          </Row>
          <Row label="Development Address">
            <EditableTextarea value={d.development_address} onChange={v => set('development_address', v)} />
          </Row>
        </tbody>
      </table>

      {/* ── Standards of Performance ─────────────────────────────────────── */}
      <SectionBar title="Standards of Performance" />
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="text-left p-3 text-xs font-bold uppercase tracking-wider" style={{ background: NAVY, color: '#fff', width: '33%' }}>
              Measure and / or System
            </th>
            <th className="text-left p-3 text-xs font-bold uppercase tracking-wider" style={{ background: NAVY, color: '#fff' }}>
              Standards of Performance
            </th>
          </tr>
        </thead>
        <tbody>
          {/* Builder's Completion — fixed, not editable */}
          <tr>
            <td className="align-top p-3 text-xs font-semibold" style={{ color: NAVY, borderBottom: '1px solid #e5e4df' }}>
              Builder's Completion
            </td>
            <td className="align-top p-3 text-sm leading-relaxed" style={{ borderBottom: '1px solid #e5e4df', color: '#2c2c2c' }}>
              <p className="mb-2">
                All works have been carried out in accordance with the approved plans, specifications, all
                non-structural parts and their components and their fastenings (walls, partitions, ceilings,
                fixtures and fittings and the like) have been installed to achieve compliance with Section 8 of
                AS 1170.4-2007 and any deviations have been disclosed to the Principal Certifier.
              </p>
              <p>
                I acknowledge that no products or materials were replaced based on the approved or relied upon
                documentation and/or correspondence to Incert Pty Ltd.
              </p>
            </td>
          </tr>

          {/* Access Report */}
          <tr>
            <td className="align-top p-3 text-xs font-semibold" style={{ color: NAVY, borderBottom: '1px solid #e5e4df' }}>
              Access Report
            </td>
            <td className="align-top p-3" style={{ borderBottom: '1px solid #e5e4df' }}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <ReportSubField label="Report Number" value={d.access_report.number} onChange={v => setNested('access_report', 'number', v)} />
                <ReportSubField label="Revision" value={d.access_report.revision} onChange={v => setNested('access_report', 'revision', v)} />
                <ReportSubField label="Date" value={d.access_report.date} onChange={v => setNested('access_report', 'date', v)} />
                <ReportSubField label="Prepared by" value={d.access_report.prepared_by} onChange={v => setNested('access_report', 'prepared_by', v)} />
              </div>
            </td>
          </tr>

          {/* Fire Engineering Report */}
          <tr>
            <td className="align-top p-3 text-xs font-semibold" style={{ color: NAVY, borderBottom: '1px solid #e5e4df' }}>
              Fire Engineering Report
            </td>
            <td className="align-top p-3" style={{ borderBottom: '1px solid #e5e4df' }}>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                <ReportSubField label="Report Number" value={d.fire_engineering_report.number} onChange={v => setNested('fire_engineering_report', 'number', v)} />
                <ReportSubField label="Revision" value={d.fire_engineering_report.revision} onChange={v => setNested('fire_engineering_report', 'revision', v)} />
                <ReportSubField label="Date" value={d.fire_engineering_report.date} onChange={v => setNested('fire_engineering_report', 'date', v)} />
                <ReportSubField label="Prepared by" value={d.fire_engineering_report.prepared_by} onChange={v => setNested('fire_engineering_report', 'prepared_by', v)} />
              </div>
            </td>
          </tr>

          <Row label="Details of any exclusions or limitations (if any)">
            <EditableTextarea value={d.exclusions_limitations} onChange={v => set('exclusions_limitations', v)} />
          </Row>
          <Row label="Other Practices, Documents or Standards relied upon for this Certification">
            <EditableTextarea value={d.other_practices_documents} onChange={v => set('other_practices_documents', v)} />
          </Row>
          <Row label="Copies attached (includes certificates from subcontractors/suppliers, manufacturers, test reports etc.)">
            <EditableTextarea value={d.copies_attached} onChange={v => set('copies_attached', v)} />
          </Row>
        </tbody>
      </table>

      {/* ── Certifying Builder ───────────────────────────────────────────── */}
      <SectionBar title="Certifying Builder" />
      <table className="w-full border-collapse">
        <tbody>
          <Row label="Full Name">
            <EditableInput value={d.builder_name} onChange={v => set('builder_name', v)} />
          </Row>
          <Row label="Company">
            <EditableInput value={d.builder_company} onChange={v => set('builder_company', v)} />
          </Row>
          <Row label="ABN">
            <EditableInput mono value={d.builder_abn} onChange={v => set('builder_abn', v)} />
          </Row>
          <Row label="Phone">
            <EditableInput mono value={d.builder_phone} onChange={v => set('builder_phone', v)} />
          </Row>
          <Row label="Address">
            <EditableTextarea value={d.builder_address} onChange={v => set('builder_address', v)} />
          </Row>
          <Row label="Licence / Accreditation / Registration Number">
            <EditableInput mono value={d.builder_licence} onChange={v => set('builder_licence', v)} />
          </Row>
          <Row label="Signature">
            <div className="max-w-sm">
              <SignaturePicker value={d.signature} onChange={v => set('signature', v)} />
            </div>
          </Row>
          <Row label="Date">
            <div className="flex items-center gap-3">
              <input
                type="date"
                value={d.date}
                onChange={e => set('date', e.target.value)}
                className={fieldClass}
                style={{ width: 160 }}
              />
              <span className="text-xs" style={{ color: '#6b7280' }}>{formatDateLong(d.date)}</span>
            </div>
          </Row>
        </tbody>
      </table>

      <p className="text-xs px-3 py-3" style={{ color: '#9ca3af' }}>
        Changes are saved automatically.
      </p>
    </div>
  );
}

function ReportSubField({ label, value, onChange }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs whitespace-nowrap" style={{ color: '#6b7280', minWidth: 80 }}>{label}</span>
      <EditableInput value={value} onChange={onChange} />
    </div>
  );
}
